import React, { useState, forwardRef } from 'react';

const Badge = forwardRef(({ 
  children, 
  variant = 'primary', 
  size = 'sm', 
  style = {}, 
  onMouseEnter,
  onMouseLeave,
  ...props 
}, ref) => {
  const [isHovered, setIsHovered] = useState(false);

  // 1. Base styles applied to all variants
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    borderRadius: '50px',
    textTransform: 'capitalize',
    transition: 'all 0.2s ease-in-out',
    cursor: variant === 'ghost' ? 'pointer' : 'inherit', 
  };

  // 2. Specific styles for each size
  const sizeStyles = {
    xxxs: {
      fontSize: 'var(--text-xs, 8px)',
      padding: '1px 6px',
      justifyContent:'space-between',
      alignItems:'center',
      columnGap:'1px',
      letterSpacing:'0.5px'
    },
    xxs: {
      fontSize: 'var(--text-xs, 11px)',
      padding: '2px 7px',
      justifyContent:'space-between',
      alignItems:'center',
      columnGap:'2px'
    },
    xs: {
      fontSize: 'var(--text-xs, 12px)',
      padding: '2px 8px',
    },
    sm: {
      fontSize: 'var(--text-sm, 14px)',
      padding: '4px 10px',
    },
    base: {
      fontSize: 'var(--text-base, 16px)',
      padding: '6px 12px',
    },
    lg: {
      fontSize: 'var(--text-lg, 18px)',
      padding: '8px 16px',
    },
    xl: {
      fontSize: 'var(--text-xl, 20px)',
      padding: '10px 20px',
    }
  };

  // 3. Specific styles for each variant using your CSS variables
  const variantStyles = {
    primary: {
      backgroundColor: 'var(--primary20)',
      border: '1px solid var(--primary20)',
      color: 'var(--primary)',
    },
    secondary: {
      backgroundColor: 'var(--secondary10)',
      border: '1px solid var(--secondary20)',
      color: 'var(--secondary)',
    },
    error: {
      backgroundColor: 'var(--red10)',
      border: '1px solid var(--red20)',
      color: 'var(--red)',
    },
    success: {
      backgroundColor: 'var(--green10)',
      border: '1px solid var(--green20)',
      color: 'var(--green)',
    },
    warning: {
      backgroundColor: 'var(--amber10)',
      border: '1px solid var(--amber20)',
      color: 'var(--amber)',
    },
    outline: {
      backgroundColor: 'transparent',
      border: '1px solid var(--medium-grey)',
      color: 'var(--font-primary)',
    },
    ghost: {
      backgroundColor: isHovered ? 'var(--primary10)' : 'transparent',
      border: '1px solid transparent',
      color: 'var(--light-medium-grey)',
    },
    gradient: {
      background: 'linear-gradient(to right, var(--primary), var(--secondary))',
      border: '1px solid transparent',
      color: 'var(--white, #ffffff)', 
    }
  };

  // 4. Merge base, size, variant, and custom styles
  const combinedStyles = {
    ...baseStyles,
    ...(sizeStyles[size] || sizeStyles.sm), 
    ...(variantStyles[variant] || variantStyles.primary),
    ...style
  };

  return (
    <span 
      ref={ref}
      style={combinedStyles}
      onMouseEnter={(e) => {
        setIsHovered(true);
        if (onMouseEnter) onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        setIsHovered(false);
        if (onMouseLeave) onMouseLeave(e);
      }}
      {...props}
    >
      {children}
    </span>
  );
});

Badge.displayName = 'Badge';

export default Badge;